import React, { memo, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DataObject from '@mui/icons-material/DataObject';
import Upload from '@mui/icons-material/Upload';
import { SwaggerFile, SwaggerFileDetails } from '../../types/swaggerFile';
import { monoSx, pillButtonSx } from '../dashboard/styles';
import SpecDropZone from './SpecDropZone';
import TagInput from './TagInput';
import { SpecInspection, inspectSpec, normalizeTag, suggestTags } from './swaggerFileModel';

export type SwaggerDialogTarget = { mode: 'upload' } | { mode: 'edit'; file: SwaggerFile };

interface SwaggerFileDialogProps {
  /** null closes the dialog. Remount (via `key`) per open to reset the draft. */
  target: SwaggerDialogTarget | null;
  /** All files, used to rank tag suggestions. */
  files: SwaggerFile[];
  onClose: () => void;
  /** Resolves true on success so the parent can close the dialog. */
  onSubmit: (details: SwaggerFileDetails, file: File | null) => Promise<boolean>;
}

// FileReader rather than Blob#text() so this also works under jsdom.
const readText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

const fieldLabelSx = { fontSize: 12, fontWeight: 600, color: 'text.secondary' } as const;

const SwaggerFileDialog: React.FC<SwaggerFileDialogProps> = ({ target, files, onClose, onSubmit }) => {
  // Keep the last target rendered while the dialog fades out.
  const lastTarget = useRef<SwaggerDialogTarget>({ mode: 'upload' });
  if (target) lastTarget.current = target;
  const shown = lastTarget.current;
  const editing = shown.mode === 'edit' ? shown.file : null;

  const [file, setFile] = useState<File | null>(null);
  const [inspection, setInspection] = useState<SpecInspection | null>(null);
  const [description, setDescription] = useState(editing?.description ?? '');
  const [tags, setTags] = useState<string[]>(editing?.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const selectedFile = useRef<File | null>(null);

  const suggestions = useMemo(() => suggestTags(files, tags), [files, tags]);

  const selectFile = async (picked: File) => {
    selectedFile.current = picked;
    setFile(picked);
    setInspection(null);
    let result: SpecInspection;
    try {
      result = inspectSpec(picked.name, await readText(picked), picked.size);
    } catch {
      result = { valid: false, error: 'Could not read file' };
    }
    // Ignore a stale read if the user picked another file meanwhile.
    if (selectedFile.current === picked) setInspection(result);
  };

  const clearFile = () => {
    selectedFile.current = null;
    setFile(null);
    setInspection(null);
  };

  const canSave = !saving && (editing ? true : Boolean(file && inspection?.valid));

  const save = async () => {
    if (!canSave) return;
    const pending = normalizeTag(tagInput);
    const finalTags = pending && !tags.includes(pending) ? [...tags, pending] : tags;
    setSaving(true);
    const ok = await onSubmit({ description: description.trim() || undefined, tags: finalTags }, file);
    if (!ok) setSaving(false);
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={saving ? undefined : onClose}
      aria-labelledby="swagger-dialog-title"
      PaperProps={{ sx: { width: '100%', maxWidth: 520, borderRadius: 5, m: 3, maxHeight: 'calc(100% - 48px)' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5, pb: 1.5, pl: 3, pr: 2.5 }}>
        <Typography id="swagger-dialog-title" component="h2" sx={{ flex: 1, fontSize: 20, fontWeight: 600 }}>
          {editing ? 'Edit file details' : 'Upload Swagger file'}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ pt: 0.5, pb: 1, px: 3, display: 'flex', flexDirection: 'column', gap: 2.25, overflow: 'auto' }}>
        {editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.25, px: 1.5, borderRadius: 3, bgcolor: 'action.hover' }}>
            <DataObject sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography noWrap title={editing.originalName} sx={{ ...monoSx, fontSize: 13, color: 'text.primary' }}>
              {editing.originalName}
            </Typography>
          </Box>
        ) : (
          <SpecDropZone file={file} inspection={inspection} onSelect={selectFile} onClear={clearFile} />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography component="label" htmlFor="swagger-description" sx={fieldLabelSx}>
            Description{' '}
            <Box component="span" sx={{ fontWeight: 400, color: 'text.disabled' }}>
              optional
            </Box>
          </Typography>
          <InputBase
            id="swagger-description"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Production spec for the September release"
            sx={{
              py: 1.25,
              px: 1.5,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              fontSize: 14,
              lineHeight: 1.45,
              '&.Mui-focused': { borderColor: 'primary.main', boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}` },
            }}
          />
        </Box>

        <TagInput tags={tags} input={tagInput} suggestions={suggestions} onTagsChange={setTags} onInputChange={setTagInput} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, pb: 2.5, px: 3 }}>
        <Button onClick={onClose} disabled={saving} sx={pillButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : editing ? <Check /> : <Upload />}
          sx={pillButtonSx}
        >
          {editing ? 'Save changes' : 'Upload'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default memo(SwaggerFileDialog);
